import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { importSPKI, CompactEncrypt } from "jose";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Target, CheckCircle, CreditCard, Lock, Shield, Crown,
  Zap, ChevronRight, Eye, EyeOff, LogOut
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const WOMPI_BASE = process.env.REACT_APP_WOMPI_BASE_URL;
const WOMPI_PUB = process.env.REACT_APP_WOMPI_PUBLIC_KEY;

const PLANS = [
  {
    id: "basico",
    name: "Básico",
    price: "17.000",
    priceCents: 1700000,
    description: "Para empresas que están empezando a contratar con el Estado.",
    features: ["Radar SECOP II completo", "Análisis IA (50 procesos/día)", "Score automático 0-100", "Alertas personalizadas", "1 usuario por cuenta"],
    color: "#64748B",
    icon: <Shield className="w-5 h-5" />,
  },
  {
    id: "premium",
    name: "Premium",
    price: "29.700",
    priceCents: 2970000,
    description: "Maximiza tus oportunidades con IA ilimitada y soporte prioritario.",
    features: ["Todo lo del plan Básico", "Análisis IA ilimitado", "Múltiples usuarios", "Perfil de empresa avanzado", "Soporte prioritario"],
    color: "#10B981",
    badge: "MÁS POPULAR",
    icon: <Crown className="w-5 h-5" />,
  },
];

async function tokenizeCard(cardData) {
  const keysResp = await fetch(`${WOMPI_BASE}/tokens/keys/tokenization`, {
    headers: { Authorization: `Bearer ${WOMPI_PUB}` },
  });
  if (!keysResp.ok) throw new Error("No se pudo inicializar el cifrado de tarjeta");
  const keysData = await keysResp.json();
  const rsaPublicKey = keysData.data.publicKey;

  const publicKey = await importSPKI(rsaPublicKey, "RSA-OAEP-256");
  const payload = new TextEncoder().encode(JSON.stringify(cardData));
  const jwe = await new CompactEncrypt(payload)
    .setProtectedHeader({ alg: "RSA-OAEP-256", enc: "A256GCM" })
    .encrypt(publicKey);

  const tokenResp = await fetch(`${WOMPI_BASE}/tokens/cards`, {
    method: "POST",
    headers: { Authorization: `Bearer ${WOMPI_PUB}`, "Content-Type": "application/json" },
    body: JSON.stringify({ payload: jwe }),
  });
  const tokenData = await tokenResp.json();
  if (!tokenResp.ok) throw new Error(tokenData?.error?.reason || "Error al tokenizar tarjeta");
  return tokenData.data.id;
}

function formatCardNumber(val) {
  return val.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
}

export default function SubscriptionExpired() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const [selectedPlan, setSelectedPlan] = useState(searchParams.get("plan") === "basico" ? "basico" : "premium");
  const [step, setStep] = useState("plan"); // plan → card → processing → success
  const [merchants, setMerchants] = useState(null);
  const [showCvc, setShowCvc] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPersonal, setAcceptedPersonal] = useState(false);
  const [loadingMerchant, setLoadingMerchant] = useState(false);

  const [card, setCard] = useState({
    number: "", card_holder: "", exp_month: "", exp_year: "", cvc: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const isTrialExpired = user?.subscription_status === "trial" || !user?.subscription_status;
  const planData = PLANS.find(p => p.id === selectedPlan);

  const fetchMerchants = async () => {
    setLoadingMerchant(true);
    try {
      const r = await fetch(`${API}/billing/merchants`);
      const data = await r.json();
      setMerchants(data);
    } catch {
      toast.error("Error cargando políticas de pago");
    } finally {
      setLoadingMerchant(false);
    }
  };

  const handleContinueToCard = async () => {
    await fetchMerchants();
    setStep("card");
  };

  const updateCard = (field, val) => {
    setCard(prev => ({ ...prev, [field]: val }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const e = {};
    const digits = card.number.replace(/\s/g, "");
    if (digits.length < 13) e.number = "Número inválido";
    if (!card.card_holder.trim()) e.card_holder = "Requerido";
    if (!/^\d{2}$/.test(card.exp_month) || +card.exp_month < 1 || +card.exp_month > 12) e.exp_month = "01-12";
    if (!/^\d{2}$/.test(card.exp_year)) e.exp_year = "YY";
    if (!/^\d{3,4}$/.test(card.cvc)) e.cvc = "3-4 dígitos";
    if (!acceptedTerms) e.terms = "Debes aceptar los términos";
    if (!acceptedPersonal) e.personal = "Debes aceptar la política de datos";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSubmitting(true);
    setStep("processing");

    try {
      const cardToken = await tokenizeCard({
        number: card.number.replace(/\s/g, ""),
        cvc: card.cvc,
        exp_month: card.exp_month,
        exp_year: card.exp_year,
        card_holder: card.card_holder,
      });

      const r = await fetch(`${API}/billing/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          card_token: cardToken,
          acceptance_token: merchants?.acceptance_token,
          accept_personal_auth: merchants?.personal_data_auth_token,
          plan: selectedPlan,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || "Error procesando pago");

      setStep("success");
      // Refresh user info
      const meResp = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (meResp.ok) {
        const meData = await meResp.json();
        setUser(meData);
        setTimeout(() => navigate("/dashboard"), 2500);
      }
    } catch (err) {
      toast.error(err.message || "Error al procesar el pago");
      setStep("card");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen text-slate-100 overflow-x-hidden" style={{ background: "#080C14" }}>
      {/* Aurora BG */}
      <div className="pointer-events-none fixed inset-0 z-0" aria-hidden>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 70% 50% at 20% 20%, rgba(16,185,129,0.10) 0%, transparent 100%)" }} />
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 60% 45% at 80% 80%, rgba(6,182,212,0.07) 0%, transparent 100%)" }} />
      </div>

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-5 py-4 border-b border-slate-800/60">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.3)" }}>
            <Target className="w-4 h-4 text-emerald-400" />
          </div>
          <span className="font-bold text-white tracking-tight">RadarLit</span>
        </div>
        {user && (
          <button onClick={logout} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-300 text-xs transition-colors">
            <LogOut className="w-3.5 h-3.5" /> Cerrar sesión
          </button>
        )}
      </header>

      <div className="relative z-10 max-w-2xl mx-auto px-4 py-10">
        <AnimatePresence mode="wait">

          {/* ─── STEP: PLAN SELECTION ─── */}
          {step === "plan" && (
            <motion.div key="plan" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }}>
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-4" style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.2)" }}>
                  <span className="text-amber-400 text-xs font-semibold">
                    {isTrialExpired ? "Tu prueba gratuita ha finalizado" : "Suscripción inactiva"}
                  </span>
                </div>
                <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-2">Elige tu plan</h1>
                <p className="text-slate-400 text-sm">El cobro se realiza mensualmente. Cancela cuando quieras.</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                {PLANS.map(plan => (
                  <motion.button
                    key={plan.id}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedPlan(plan.id)}
                    className="relative text-left rounded-2xl p-6 transition-all duration-200"
                    style={{
                      background: selectedPlan === plan.id
                        ? plan.id === "premium" ? "rgba(16,185,129,0.08)" : "rgba(100,116,139,0.08)"
                        : "rgba(15,23,42,0.6)",
                      border: selectedPlan === plan.id
                        ? `2px solid ${plan.color}`
                        : "2px solid rgba(51,65,85,0.5)",
                      boxShadow: selectedPlan === plan.id && plan.id === "premium"
                        ? "0 0 30px rgba(16,185,129,0.12)"
                        : "none",
                    }}
                    data-testid={`plan-${plan.id}`}
                  >
                    {plan.badge && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-black tracking-wider px-3 py-0.5 rounded-full"
                        style={{ background: "linear-gradient(135deg, #059669, #0891B2)", color: "#fff" }}>
                        {plan.badge}
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${plan.color}15`, color: plan.color }}>
                          {plan.icon}
                        </div>
                        <span className="font-bold text-slate-100">{plan.name}</span>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${selectedPlan === plan.id ? "border-emerald-400 bg-emerald-400" : "border-slate-600"}`}>
                        {selectedPlan === plan.id && <div className="w-2 h-2 rounded-full bg-white" />}
                      </div>
                    </div>
                    <div className="mb-2">
                      <span className="text-3xl font-black font-mono text-white">${plan.price}</span>
                      <span className="text-slate-400 text-xs ml-1">COP/mes</span>
                    </div>
                    <p className="text-slate-400 text-xs mb-4">{plan.description}</p>
                    <ul className="space-y-1.5">
                      {plan.features.map(f => (
                        <li key={f} className="flex items-center gap-2 text-xs text-slate-300">
                          <CheckCircle className="w-3.5 h-3.5 shrink-0 text-emerald-400" />{f}
                        </li>
                      ))}
                    </ul>
                  </motion.button>
                ))}
              </div>

              <div className="text-center text-xs text-slate-500 mb-6 flex items-center justify-center gap-1.5">
                <Lock className="w-3 h-3" /> Pago seguro con Wompi · Tu tarjeta no se guarda en nuestros servidores
              </div>

              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleContinueToCard}
                disabled={loadingMerchant}
                className="w-full flex items-center justify-center gap-2.5 font-extrabold py-4 rounded-xl text-sm"
                style={{
                  background: "linear-gradient(135deg, #10B981, #06B6D4)",
                  color: "#020617",
                  boxShadow: "0 0 30px rgba(16,185,129,0.35)",
                  opacity: loadingMerchant ? 0.7 : 1,
                }}
                data-testid="continue-to-payment"
              >
                {loadingMerchant ? (
                  <div className="w-4 h-4 border-2 border-slate-800 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>Continuar con {planData?.name} — ${planData?.price} COP/mes <ChevronRight className="w-4 h-4" /></>
                )}
              </motion.button>
            </motion.div>
          )}

          {/* ─── STEP: CARD FORM ─── */}
          {step === "card" && (
            <motion.div key="card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }}>
              <div className="flex items-center gap-3 mb-6">
                <button onClick={() => setStep("plan")} className="text-slate-400 hover:text-white transition-colors text-sm">
                  ← Volver
                </button>
                <div className="flex-1" />
                <div className="flex items-center gap-1.5 text-xs text-slate-400 rounded-lg px-3 py-1.5 border border-slate-700/50">
                  <div className="w-4 h-4 rounded flex items-center justify-center" style={{ background: planData?.id === "premium" ? "rgba(16,185,129,0.15)" : "rgba(100,116,139,0.15)", color: planData?.color }}>
                    {planData?.icon}
                  </div>
                  Plan {planData?.name} · ${planData?.price} COP/mes
                </div>
              </div>

              <h2 className="text-2xl font-extrabold text-white mb-1">Datos de pago</h2>
              <p className="text-slate-400 text-sm mb-6">Tu tarjeta es cifrada directamente con Wompi. Nosotros no almacenamos números de tarjeta.</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="rounded-2xl p-5 space-y-4" style={{ background: "rgba(15,23,42,0.7)", border: "1px solid rgba(51,65,85,0.5)" }}>
                  <div className="flex items-center gap-2 mb-1">
                    <CreditCard className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm font-semibold text-slate-200">Información de tarjeta</span>
                  </div>

                  {/* Card number */}
                  <div>
                    <label className="text-xs font-medium text-slate-400 mb-1.5 block">Número de tarjeta</label>
                    <input
                      value={card.number}
                      onChange={e => updateCard("number", formatCardNumber(e.target.value))}
                      placeholder="4242 4242 4242 4242"
                      inputMode="numeric"
                      autoComplete="cc-number"
                      data-testid="card-number"
                      className="w-full rounded-xl px-4 py-3 text-sm font-mono text-slate-100 placeholder-slate-600 outline-none transition-all"
                      style={{
                        background: "rgba(2,6,23,0.6)",
                        border: errors.number ? "1px solid #EF4444" : "1px solid rgba(51,65,85,0.6)",
                      }}
                      onFocus={e => { e.target.style.borderColor = "rgba(16,185,129,0.6)"; }}
                      onBlur={e => { e.target.style.borderColor = errors.number ? "#EF4444" : "rgba(51,65,85,0.6)"; }}
                    />
                    {errors.number && <p className="text-red-400 text-xs mt-1">{errors.number}</p>}
                  </div>

                  {/* Cardholder */}
                  <div>
                    <label className="text-xs font-medium text-slate-400 mb-1.5 block">Nombre en la tarjeta</label>
                    <input
                      value={card.card_holder}
                      onChange={e => updateCard("card_holder", e.target.value.toUpperCase())}
                      placeholder="NOMBRE APELLIDO"
                      autoComplete="cc-name"
                      data-testid="card-holder"
                      className="w-full rounded-xl px-4 py-3 text-sm font-mono text-slate-100 placeholder-slate-600 outline-none transition-all"
                      style={{
                        background: "rgba(2,6,23,0.6)",
                        border: errors.card_holder ? "1px solid #EF4444" : "1px solid rgba(51,65,85,0.6)",
                      }}
                      onFocus={e => { e.target.style.borderColor = "rgba(16,185,129,0.6)"; }}
                      onBlur={e => { e.target.style.borderColor = errors.card_holder ? "#EF4444" : "rgba(51,65,85,0.6)"; }}
                    />
                    {errors.card_holder && <p className="text-red-400 text-xs mt-1">{errors.card_holder}</p>}
                  </div>

                  {/* Expiry + CVC */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-400 mb-1.5 block">Mes (MM)</label>
                      <input
                        value={card.exp_month}
                        onChange={e => updateCard("exp_month", e.target.value.replace(/\D/g, "").slice(0, 2))}
                        placeholder="07"
                        inputMode="numeric"
                        autoComplete="cc-exp-month"
                        data-testid="card-exp-month"
                        className="w-full rounded-xl px-4 py-3 text-sm font-mono text-center text-slate-100 placeholder-slate-600 outline-none transition-all"
                        style={{
                          background: "rgba(2,6,23,0.6)",
                          border: errors.exp_month ? "1px solid #EF4444" : "1px solid rgba(51,65,85,0.6)",
                        }}
                        onFocus={e => { e.target.style.borderColor = "rgba(16,185,129,0.6)"; }}
                        onBlur={e => { e.target.style.borderColor = errors.exp_month ? "#EF4444" : "rgba(51,65,85,0.6)"; }}
                      />
                      {errors.exp_month && <p className="text-red-400 text-[10px] mt-1">{errors.exp_month}</p>}
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-400 mb-1.5 block">Año (AA)</label>
                      <input
                        value={card.exp_year}
                        onChange={e => updateCard("exp_year", e.target.value.replace(/\D/g, "").slice(0, 2))}
                        placeholder="29"
                        inputMode="numeric"
                        autoComplete="cc-exp-year"
                        data-testid="card-exp-year"
                        className="w-full rounded-xl px-4 py-3 text-sm font-mono text-center text-slate-100 placeholder-slate-600 outline-none transition-all"
                        style={{
                          background: "rgba(2,6,23,0.6)",
                          border: errors.exp_year ? "1px solid #EF4444" : "1px solid rgba(51,65,85,0.6)",
                        }}
                        onFocus={e => { e.target.style.borderColor = "rgba(16,185,129,0.6)"; }}
                        onBlur={e => { e.target.style.borderColor = errors.exp_year ? "#EF4444" : "rgba(51,65,85,0.6)"; }}
                      />
                      {errors.exp_year && <p className="text-red-400 text-[10px] mt-1">{errors.exp_year}</p>}
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-400 mb-1.5 block">CVC</label>
                      <div className="relative">
                        <input
                          value={card.cvc}
                          onChange={e => updateCard("cvc", e.target.value.replace(/\D/g, "").slice(0, 4))}
                          type={showCvc ? "text" : "password"}
                          placeholder="•••"
                          inputMode="numeric"
                          autoComplete="cc-csc"
                          data-testid="card-cvc"
                          className="w-full rounded-xl px-4 py-3 text-sm font-mono text-center text-slate-100 placeholder-slate-600 outline-none transition-all pr-10"
                          style={{
                            background: "rgba(2,6,23,0.6)",
                            border: errors.cvc ? "1px solid #EF4444" : "1px solid rgba(51,65,85,0.6)",
                          }}
                          onFocus={e => { e.target.style.borderColor = "rgba(16,185,129,0.6)"; }}
                          onBlur={e => { e.target.style.borderColor = errors.cvc ? "#EF4444" : "rgba(51,65,85,0.6)"; }}
                        />
                        <button type="button" onClick={() => setShowCvc(!showCvc)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors">
                          {showCvc ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                      {errors.cvc && <p className="text-red-400 text-[10px] mt-1">{errors.cvc}</p>}
                    </div>
                  </div>
                </div>

                {/* Policies */}
                {merchants && (
                  <div className="rounded-2xl p-5 space-y-3" style={{ background: "rgba(15,23,42,0.5)", border: "1px solid rgba(51,65,85,0.4)" }}>
                    <p className="text-xs font-semibold text-slate-400 mb-2">Políticas requeridas por Wompi</p>
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input type="checkbox" checked={acceptedTerms} onChange={e => { setAcceptedTerms(e.target.checked); setErrors(p => ({ ...p, terms: "" })); }} className="mt-0.5 accent-emerald-500" data-testid="accept-terms" />
                      <span className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors">
                        He leído y acepto los{" "}
                        <a href={merchants.permalink} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline">
                          Términos y condiciones
                        </a>{" "}
                        de Wompi.
                      </span>
                    </label>
                    {errors.terms && <p className="text-red-400 text-xs">{errors.terms}</p>}
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input type="checkbox" checked={acceptedPersonal} onChange={e => { setAcceptedPersonal(e.target.checked); setErrors(p => ({ ...p, personal: "" })); }} className="mt-0.5 accent-emerald-500" data-testid="accept-personal" />
                      <span className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors">
                        Autorizo el{" "}
                        <a href={merchants.personal_data_permalink} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline">
                          tratamiento de mis datos personales
                        </a>{" "}
                        conforme a la política de Wompi.
                      </span>
                    </label>
                    {errors.personal && <p className="text-red-400 text-xs">{errors.personal}</p>}
                  </div>
                )}

                {/* Summary */}
                <div className="flex items-center justify-between rounded-xl px-4 py-3" style={{ background: "rgba(16,185,129,0.05)", border: "1px solid rgba(16,185,129,0.15)" }}>
                  <span className="text-slate-400 text-sm">Total mensual</span>
                  <span className="text-white font-extrabold font-mono">${planData?.price} COP</span>
                </div>

                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2.5 font-extrabold py-4 rounded-xl text-sm"
                  style={{
                    background: submitting ? "rgba(16,185,129,0.4)" : "linear-gradient(135deg, #10B981, #06B6D4)",
                    color: "#020617",
                    boxShadow: submitting ? "none" : "0 0 30px rgba(16,185,129,0.35)",
                    transition: "all 0.2s",
                  }}
                  data-testid="submit-payment"
                >
                  {submitting ? (
                    <div className="w-4 h-4 border-2 border-slate-800 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <><Lock className="w-4 h-4" /> Activar Plan {planData?.name}</>
                  )}
                </motion.button>

                <p className="text-center text-xs text-slate-600">
                  <Lock className="w-3 h-3 inline mr-1" />
                  Pago cifrado SSL · Powered by Wompi
                </p>
              </form>
            </motion.div>
          )}

          {/* ─── STEP: PROCESSING ─── */}
          {step === "processing" && (
            <motion.div key="processing" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-20">
              <div className="w-16 h-16 border-2 border-emerald-500/30 border-t-emerald-400 rounded-full animate-spin mx-auto mb-6" />
              <h2 className="text-xl font-bold text-white mb-2">Procesando pago...</h2>
              <p className="text-slate-400 text-sm">Cifrado y verificando con Wompi</p>
            </motion.div>
          )}

          {/* ─── STEP: SUCCESS ─── */}
          {step === "success" && (
            <motion.div key="success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-16">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ background: "rgba(16,185,129,0.15)", border: "2px solid rgba(16,185,129,0.4)" }}
              >
                <CheckCircle className="w-10 h-10 text-emerald-400" />
              </motion.div>
              <h2 className="text-2xl font-extrabold text-white mb-2">¡Plan activado!</h2>
              <p className="text-slate-400 text-sm mb-4">
                Tu plan <span className="text-emerald-400 font-semibold">{planData?.name}</span> está activo. Redirigiendo al dashboard...
              </p>
              <div className="w-8 h-8 border-2 border-emerald-500/30 border-t-emerald-400 rounded-full animate-spin mx-auto" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
